import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { SecondaryButtonClassname } from '../../../../components/buttons';
import { Select } from '../../../../components/forms';
import { linkClassName } from '../../../../components/links';
import { Popover } from '../../../../components/popover/popover';
import { PopoverHeader } from '../../../../components/toolbar/popover-header';
import type { OrderOptions } from '../../../../data/order-options';
import {
  type ReorderWeeks,
  reorderWeekOptions,
  type ThresholdWeeks,
  thresholdWeekOptions,
} from '../../../../data/order-settings';
import { OptionWrapper } from './option-wrapper';

type ReorderSettingsPopoverProps = {
  options: OrderOptions;
  setOptions: (options: OrderOptions) => void;
  storeId: string;
  vendorId: string;
};

export function ReorderSettingsPopover({
  options,
  setOptions,
  storeId,
  vendorId,
}: ReorderSettingsPopoverProps) {
  return (
    <Popover>
      <Popover.Trigger>
        <button
          type="button"
          className={`${SecondaryButtonClassname} flex items-center justify-center`}
        >
          <span className="text-icon">
            <Cog6ToothIcon className="size-5" />
          </span>
          <span>Reorder Settings</span>
        </button>
      </Popover.Trigger>
      <Popover.Content className="min-w-56" align="start">
        <PopoverHeader label="Reorder Settings" />
        <div className="grid grid-flow-row gap-4 justify-items-start">
          <OptionWrapper label="QOH Threshold Weeks" direction="vertical">
            <Select<ThresholdWeeks>
              value={options.qoh_threshold_weeks}
              onChange={(qoh_threshold_weeks) => {
                setOptions({ ...options, qoh_threshold_weeks });
              }}
              options={thresholdWeekOptions}
              className="w-24"
            />
            <p className="text-sm text-gray-500 max-w-48">
              When the quantity on hand for a product is less than this number of weeks based on the
              last 30 days of sales, a reorder is triggered for the product.
            </p>
          </OptionWrapper>
          <OptionWrapper label="Reorder Weeks" direction="vertical">
            <Select<ReorderWeeks>
              value={options.reorder_weeks}
              onChange={(reorder_weeks) => {
                setOptions({ ...options, reorder_weeks });
              }}
              options={reorderWeekOptions}
              className="w-24"
            />
            <p className="text-sm text-gray-500 max-w-48">
              The number of weeks of inventory to re-order based on the last 30 days of sales.
            </p>
          </OptionWrapper>
          <div className="max-w-48 text-sm text-gray-500 border-t border-gray-300 pt-2">
            You can set defaults for these values in the{' '}
            <Link
              to="/stores/$storeId/vendors/$vendorId/edit"
              params={{ storeId, vendorId }}
              className={linkClassName}
            >
              Vendor Settings
            </Link>{' '}
            page.
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}
